// Brand slice 1 (D157) — the identity kit: DOM builders for the signature components the design
// handoff (design_handoff_fafi_identity) defines once and the whole product reuses. Pixels live in
// theme.css (classes carry the hover/active/capture states cssText cannot express); these builders own
// structure and text. textContent only (§15.1) — user strings can never become markup. The svgIcon
// register is the start of the flat inline-SVG set that replaces OS emoji product-wide.

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Namespace-aware SVG builder (innerHTML is banned — §15.1). Shared by the kit and the home screen. */
export function svgEl<T extends SVGElement>(doc: Document, tag: string, attrs: Record<string, string | number>): T {
  const e = doc.createElementNS(SVG_NS, tag) as T;
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

export type KeycapState = 'capture' | 'unbound';

/** The key-cap chip. Variable width by content; `—` auto-reads as unbound (keyLabel('') renders it). */
export function keycap(doc: Document, label: string, opts: { state?: KeycapState; small?: boolean } = {}): HTMLSpanElement {
  const k = doc.createElement('span');
  k.className = 'fafi-keycap';
  if (opts.small) k.classList.add('fafi-keycap--sm');
  const state = opts.state ?? (label === '—' ? 'unbound' : undefined);
  if (state) k.classList.add(`fafi-keycap--${state}`);
  k.textContent = label;
  return k;
}

/** The letter-spaced caps motif (STAMINA · KICK-OFF · CONTROLS), formalised. */
export function capsTag(doc: Document, label: string, variant: 'quiet' | 'boxed' | 'loud'): HTMLSpanElement {
  const t = doc.createElement('span');
  t.className = 'fafi-tag';
  if (variant !== 'quiet') t.classList.add(`fafi-tag--${variant}`);
  t.textContent = label;
  return t;
}

/** The ONE element allowed at primary weight on a screen (amber, display face). */
export function primaryBtn(doc: Document, label: string, onClick: () => void): HTMLButtonElement {
  const b = doc.createElement('button');
  b.type = 'button';
  b.className = 'fafi-btn-primary';
  b.textContent = label;
  b.onclick = onClick;
  return b;
}

export interface SecondaryBtn {
  readonly el: HTMLButtonElement;
  /** Update the badge pill; 0 hides it (the "N new" count is computed, never stored). */
  setBadge(n: number): void;
}

/** Secondary tier — quiet plate, amber on hover; optional "N new" badge pill. */
export function secondaryBtn(doc: Document, label: string, onClick: () => void, badgeCount?: number): SecondaryBtn {
  const b = doc.createElement('button');
  b.type = 'button';
  b.className = 'fafi-btn-secondary';
  const text = doc.createElement('span');
  text.textContent = label;
  b.append(text);
  b.onclick = onClick;
  let badge: HTMLSpanElement | null = null;
  const setBadge = (n: number): void => {
    if (!badge) return;
    badge.textContent = `${n} new`;
    badge.style.display = n > 0 ? '' : 'none';
  };
  if (badgeCount !== undefined) {
    badge = doc.createElement('span');
    badge.className = 'fafi-badge';
    b.append(badge);
    setBadge(badgeCount);
  }
  return { el: b, setBadge };
}

/**
 * The wordmark lockup. `centred` (home): FAFI at display XL, the tagline between two amber rules.
 * `left` (panels): the rule sits left of the tagline only.
 */
export function wordmarkLockup(doc: Document, mode: 'centred' | 'left'): HTMLDivElement {
  const w = doc.createElement('div');
  w.className = mode === 'centred' ? 'fafi-wordmark fafi-wordmark--centred' : 'fafi-wordmark';
  const word = doc.createElement('div');
  word.className = 'fafi-wordmark-word';
  word.textContent = 'FAFI';
  const row = doc.createElement('div');
  row.className = 'fafi-wordmark-row';
  const rule = (): HTMLDivElement => {
    const r = doc.createElement('div');
    r.className = 'fafi-wordmark-rule';
    return r;
  };
  const tagline = doc.createElement('div');
  tagline.className = 'fafi-wordmark-tagline';
  tagline.textContent = 'FAIR & FRIENDLY INTERNET FOOTBALL';
  if (mode === 'centred') row.append(rule(), tagline, rule());
  else row.append(rule(), tagline);
  w.append(word, row);
  return w;
}

/**
 * The monogram: amber rounded square + chalk-line ball. Exact geometry from the handoff. At 16px the
 * spokes are dropped and the stroke steps up so the mark survives a favicon slot.
 */
export function monogram(doc: Document, size: 64 | 32 | 16): SVGSVGElement {
  const m = svgEl<SVGSVGElement>(doc, 'svg', { width: size, height: size, viewBox: '0 0 32 32' });
  const small = size === 16;
  m.append(svgEl(doc, 'rect', { width: 32, height: 32, rx: small ? 8 : 7, fill: '#ffb224' }));
  const sw = small ? 3.5 : size === 32 ? 2.4 : 2;
  m.append(svgEl(doc, 'circle', { cx: 16, cy: 16, r: 10, fill: 'none', stroke: '#0a1608', 'stroke-width': sw }));
  const pentagon = small ? 'M16 11 L20.5 14.4 L18.8 19.8 L13.2 19.8 L11.5 14.4 Z' : 'M16 12 L19.8 14.8 L18.4 19.2 L13.6 19.2 L12.2 14.8 Z';
  m.append(svgEl(doc, 'path', { d: pentagon, fill: '#0a1608' }));
  if (!small) {
    m.append(
      svgEl(doc, 'path', {
        d: 'M16 12 L16 6 M19.8 14.8 L25 13 M18.4 19.2 L21.8 23.8 M13.6 19.2 L10.2 23.8 M12.2 14.8 L7 13',
        stroke: '#0a1608',
        'stroke-width': sw,
        fill: 'none',
      }),
    );
  }
  return m;
}

export type IconName = 'person' | 'pencil';

/** Flat single-weight inline icons (stroke = currentColor, ink preset per glyph, overridable). */
export function svgIcon(doc: Document, name: IconName, size: number): SVGSVGElement {
  const i = svgEl<SVGSVGElement>(doc, 'svg', { width: size, height: size, viewBox: '0 0 16 16', fill: 'none' });
  if (name === 'person') {
    i.style.color = 'var(--ink-2)';
    i.append(svgEl(doc, 'circle', { cx: 8, cy: 5.5, r: 3, stroke: 'currentColor', 'stroke-width': 1.5 }));
    i.append(svgEl(doc, 'path', { d: 'M2.5 14 C3.2 10.8 5.3 9.5 8 9.5 C10.7 9.5 12.8 10.8 13.5 14', stroke: 'currentColor', 'stroke-width': 1.5 }));
  } else {
    i.style.color = 'var(--ink-3)';
    i.append(
      svgEl(doc, 'path', {
        d: 'M11.5 2.5 L13.5 4.5 L5.5 12.5 L2.8 13.2 L3.5 10.5 Z',
        stroke: 'currentColor',
        'stroke-width': 1.5,
        'stroke-linejoin': 'round',
      }),
    );
  }
  return i;
}

/** An action link as a real <button> (keyboard + a11y). Amber by default; `muted` = ink-2 → amber. */
export function link(doc: Document, label: string, onClick: () => void, opts: { muted?: boolean } = {}): HTMLButtonElement {
  const l = doc.createElement('button');
  l.type = 'button';
  l.className = opts.muted ? 'fafi-link fafi-link--muted' : 'fafi-link';
  l.textContent = label;
  l.onclick = onClick;
  return l;
}
